import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Stack,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Link,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import { useParams, useNavigate } from 'react-router-dom';
import config from '../../Config';

const formatDate = (isoString) => {
  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };
  return new Date(isoString).toLocaleDateString(undefined, options);
};

const statusColors = {
  Confirmed: 'success',
  Pending: 'warning',
  Cancelled: 'error',
  Delivered: 'primary',
};

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchOrderDetail = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${config.apiBaseUrl}/admin/getOrder/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await res.json();

        if (res.ok) {
          setOrder(data.order);
        } else {
          setError(data.message || 'Failed to fetch order details.');
        }
      } catch (err) {
        setError('Error fetching order details.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetail();
  }, [id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!order) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <Typography variant="h6" color="textSecondary">
          Order not found.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mx: 'auto', width: '100%', maxWidth: 1200, mt: 3, pb: 5 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Button
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          sx={{
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          Back
        </Button>
        <Typography variant={isSmDown ? 'h5' : 'h4'} fontWeight="bold">
          Order Details
        </Typography>
      </Stack>

      <Paper
        elevation={4}
        sx={{
          p: 4,
          borderRadius: 4,
          background: theme.palette.background.paper,
        }}
      >
        <Stack spacing={4}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <InfoOutlinedIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Basic Information
              </Typography>
            </Stack>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Order ID
                </Typography>
                <Typography fontWeight="bold">{order.orderId}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={order.status}
                  color={statusColors[order.status] || 'default'}
                  sx={{ fontWeight: 'bold', mt: 0.5 }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Order Date
                </Typography>
                <Typography>{formatDate(order.createdAt)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Grand Total
                </Typography>
                <Typography fontWeight="bold" color="primary">
                  £{order.grandTotal.toLocaleString('en-IN')}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {order.property && (
            <>
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <ApartmentOutlinedIcon color="action" />
                  <Typography variant="h6" fontWeight="bold">
                    Property Details
                  </Typography>
                </Stack>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Property ID
                    </Typography>
                    <Link
                      component="button"
                      type="button"
                      onClick={() => navigate(`/propertydetail/${order.property.propertyID}`)}
                      underline="hover"
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        textAlign: 'left',
                      }}
                    >
                      {order.property.propertyID}
                    </Link>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Property Name
                    </Typography>
                    <Typography fontWeight="bold">{order.property.propertyName}</Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider />
            </>
          )}

          <Box>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <EventOutlinedIcon color="secondary" />
              <Typography variant="h6" fontWeight="bold">
                Reservation Details
              </Typography>
            </Stack>

            {order.reservation ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: theme.palette.action.hover,
                }}
              >
                <Stack spacing={1}>
                  <Typography fontWeight="bold">{order.reservation.guestName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Email: {order.reservation.guestEmail}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Dates: {formatDate(order.reservation.startDate)} -{' '}
                    {formatDate(order.reservation.endDate)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Status: {order.reservation.status}
                  </Typography>
                </Stack>
              </Paper>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No reservation linked with this order.
              </Typography>
            )}
          </Box>

          <Divider />

          <Box>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <ShoppingCartOutlinedIcon color="success" />
              <Typography variant="h6" fontWeight="bold">
                Products
              </Typography>
            </Stack>

            <Grid container spacing={3}>
              {order.products.map((product, idx) => (
                <Grid item key={idx} xs={12} sm={6} md={4} lg={3} sx={{ display: 'flex' }}>
                  <Card
                    sx={{
                      flex: 1,
                      minWidth: 250,
                      borderRadius: 3,
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      boxShadow: 3,
                      '&:hover': {
                        boxShadow: 6,
                        transform: 'translateY(-3px)',
                        transition: '0.3s ease',
                      },
                    }}
                  >
                    {product.image ? (
                      <CardMedia
                        component="img"
                        image={product.image}
                        alt={product.name}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '';
                        }}
                        sx={{
                          height: 180,
                          width: '100%',
                          objectFit: 'contain',
                          bgcolor: 'action.hover',
                          p: 1,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 180,
                          width: '100%',
                          bgcolor: 'action.hover',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <ImageNotSupportedOutlinedIcon
                          sx={{ fontSize: 60, color: 'text.disabled' }}
                        />
                      </Box>
                    )}

                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold" noWrap sx={{ mb: 0.5 }}>
                        {product.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        £{product.price.toLocaleString('en-IN')} × {product.quantity}
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        color="primary"
                        sx={{ mt: 0.5 }}
                      >
                        £{product.total.toLocaleString('en-IN')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
